import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { viewFor, type PortalView, type PortalViewGone } from '../lib/portal-view';

// ─── La vista del portal activa en ESTA pestaña ─────────────────────────────
// En sessionStorage —no localStorage—: sobrevive a recargar la página, pero es
// de esta pestaña y se va al cerrarla. No guarda datos del alumno: solo el id
// de la vista (que el servidor valida en cada lectura), su código L-######, la
// persona, cuándo vence y QUIÉN la abrió.
//
// ATADA A QUIEN LA ABRIÓ. Si en la misma pestaña cierra la sesión y entra otra
// persona —un alumno, por ejemplo—, la vista no existe para ella: nadie la lee
// sin pasar por `viewFor` con el uid de la sesión, y se borra en cuanto cambia
// el usuario o se cierra la sesión. El uid de la sesión lo informa
// `AuthProvider` (setSessionUid) en cada cambio; este módulo no importa
// Firebase, para que quien lo usa —el wrapper de callables— tampoco tenga que.

interface PortalViewSlice {
  view: PortalView | null;
  /** El servidor dijo que la vista ya no sirve, y por qué. */
  gone: PortalViewGone | null;
  /** El uid de la sesión actual (no se guarda: lo vuelve a informar la auth). */
  sessionUid: string | null;
  start: (view: PortalView) => void;
  markGone: (why: PortalViewGone) => void;
  exit: () => void;
  /** Cada cambio de usuario: una vista que no es suya se borra. */
  setSessionUid: (uid: string | null) => void;
}

export const usePortalViewStore = create<PortalViewSlice>()(
  persist(
    (set, get) => ({
      view: null,
      gone: null,
      sessionUid: null,
      start: (view) => set({ view, gone: null }),
      markGone: (why) => set({ gone: why }),
      exit: () => set({ view: null, gone: null }),
      setSessionUid: (uid) => {
        const { view } = get();
        if (view && !viewFor(view, uid)) set({ view: null, gone: null, sessionUid: uid });
        else set({ sessionUid: uid });
      },
    }),
    {
      name: 'dosfilos-portal-view',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (s) => ({ view: s.view, gone: s.gone }),
    },
  ),
);

/** La vista activa de ESTA sesión, si la hay (para quien no es un componente). */
export function currentPortalView(): PortalView | null {
  const { view, sessionUid } = usePortalViewStore.getState();
  return viewFor(view, sessionUid);
}

/** La vista activa de ESTA sesión, para los componentes. */
export function usePortalView(): PortalView | null {
  return usePortalViewStore((s) => viewFor(s.view, s.sessionUid));
}

/** True mientras el personal mira el portal de un alumno: las páginas ocultan
 *  sus acciones y no hacen escrituras automáticas (marcar visto, notas vistas,
 *  token del calendario). El servidor las rechazaría igual. */
export function usePortalViewing(): boolean {
  return usePortalView() !== null;
}

/** Para las claves de react-query del portal: lo de una vista nunca se cruza
 *  con la caché propia, aunque `removeQueries` no alcanzara a correr. */
export function usePortalCacheScope(): string {
  return usePortalView()?.viewId ?? 'own';
}
